import styled from "@emotion/styled/macro";
import {
  Banner,
  BannerTypes,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { ReactElement, useMemo } from "react";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { formatCurrency } from "utils/formatHelpers";
import { BUILDER_OFFER_IDENTIFICATION_MESSAGE } from "constants/newHomes";
import { isBuilderOffer } from "utils/builderOffer";

type OfferReviewBannersProps = {
  offer: ReviewOffer;
  preferredLanguage?: string;
  viewDocLoading?: boolean;
  onViewDocument: () => void;
  utilityBillPastDueBalanceFlowIsEnabled: boolean;
  utilityCompany?: string;
  utilityPastDueBalance?: number;
};

const OfferReviewBanners = ({
  offer,
  preferredLanguage,
  viewDocLoading,
  onViewDocument,
  utilityBillPastDueBalanceFlowIsEnabled,
  utilityCompany,
  utilityPastDueBalance,
}: OfferReviewBannersProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const banners = useMemo(() => {
    const messages = [] as ReactElement[];

    if (offer.isSigned) {
      messages.push(
        <Banner
          key="signed"
          bannerType={BannerTypes.SuccessLight}
          show={true}
          showClose={false}
        >
          This is the signed offer.
        </Banner>
      );
    }
    if (isBuilderOffer(offer)) {
      messages.push(
        <Banner
          key="builder-offer"
          bannerType={BannerTypes.InfoLight}
          show={true}
          showClose={false}
        >
          {BUILDER_OFFER_IDENTIFICATION_MESSAGE}
        </Banner>
      );
    }
    if (preferredLanguage === "Spanish" && !viewDocLoading) {
      messages.push(
        <Banner
          key="spanish"
          bannerType={BannerTypes.InfoLight}
          title="Para ver los detalles de la oferta en Español, por favor consulta el contrato de Sunrun."
          show={true}
          showClose={false}
          primaryAction={{
            title: "Ver contrato en Español",
            action: onViewDocument,
            testId: "spanish-contract-button",
          }}
        >
          To view the offer details in Spanish, please refer to the Sunrun
          agreement.
        </Banner>
      );
    }

    if (
      utilityBillPastDueBalanceFlowIsEnabled &&
      utilityCompany &&
      utilityPastDueBalance
    ) {
      messages.push(
        <Banner
          bannerType={BannerTypes.WarningLight}
          show={true}
          title="Utility bill may have a past due amount. This could risk reaching Sunrun Approval."
          showClose={false}
          showIcon={true}
        >
          Our system indicates the utility bill shows a{" "}
          <b>
            past due amount to {utilityCompany} of{" "}
            {formatCurrency(utilityPastDueBalance)}.{" "}
          </b>
          Sunrun approval requires utility bill past due amount to be less than
          required thresholds, approval decision will occur at submission
          review.
        </Banner>
      );
    }

    return messages;
  }, [
    offer,
    onViewDocument,
    preferredLanguage,
    utilityBillPastDueBalanceFlowIsEnabled,
    utilityCompany,
    utilityPastDueBalance,
    viewDocLoading,
  ]);

  if (banners.length === 0) {
    return null;
  }

  return (
    <BannerContainer isMobile={isMobile}>
      {banners.map((banner, index) => (
        <div key={banner.key ?? index}>{banner}</div>
      ))}
    </BannerContainer>
  );
};

const BannerContainer = styled.div<{ isMobile: boolean }>`
  max-width: 1005px;
  margin: 0 auto;
  gap: 16px;
  display: flex;
  flex-direction: column;
  padding: ${({ isMobile }) => (isMobile ? "24px 16px 0px" : "48px 64px 0px")};
`;

export { OfferReviewBanners };
